import { EMPTY_TABLE_STATE, type ColumnState, type TableState } from '@crm/contracts';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type Cell,
  type Column,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { describeError } from '../../lib/api.ts';
import { cn } from '../../lib/utils.ts';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { ColumnsMenu } from './columns-menu.tsx';
import { useDebounced } from './use-debounced.ts';
import type { PageRequest, PageResult, RecordSource, SortState } from './types.ts';

/**
 * RecordTable (RELAY-937, RELAY-942): one generic table for every list in the CRM.
 *
 *  - TanStack Table for columns/sorting state, TanStack Virtual for the rows: only the rows
 *    in the viewport (plus overscan) exist in the DOM, so a 100k-row data set scrolls like a
 *    40-row one.
 *  - Sort and filter are SERVER-side: changing either re-queries the source from the first
 *    page. The table never sorts what it holds, because what it holds is never the whole set.
 *  - Two paging modes, chosen per user (Settings → Pagination): `infinite` appends the next
 *    page when the user nears the end; `paged` shows one page with Previous/Next over a
 *    cursor stack (cursors are opaque and forward-only, so "previous" replays the stack).
 *  - Columns can be hidden, reordered, resized and pinned left (RELAY-942). All of that plus
 *    the sort and the query is ONE `TableState` (contracts), which the page may own
 *    (`tableState` + `onTableStateChange`) so it can live in the URL and in saved views; left
 *    uncontrolled, the table keeps it internally.
 *  - Column widths follow their content: a column the user has not resized is as wide as its
 *    widest header or loaded cell (every loaded row, not just the rendered ones), clamped to
 *    the column's `minSize` / `maxSize` (default 72–320px), and recomputed when a page loads
 *    or the data set changes. A cell's text is what its `cell` function returns when that is a
 *    string or number, else its accessor value; so cell functions must not call hooks (render a
 *    component that does). A column with neither keeps its `size`, as does one with
 *    `meta: { autoSize: false }`; `meta: { autoSize: <px> }` adds room for a pill or an icon.
 *    A dragged width is stored in `columns.sizes` and always wins. The last column fills any
 *    space left in the viewport.
 */
export interface RecordTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  source: RecordSource<T>;
  getRowId: (row: T) => string;
  mode?: 'infinite' | 'paged';
  pageSize?: number;
  rowHeight?: number;
  /** Height of the scroll viewport (CSS). */
  height?: string;
  filterPlaceholder?: string;
  emptyMessage?: ReactNode;
  /** Initial viewport size for the virtualizer before layout runs (jsdom has no layout). */
  initialRect?: { width: number; height: number };
  /** Rows from the end at which the next page is requested. */
  prefetchThreshold?: number;
  onRowClick?: (row: T) => void;
  /** Controlled table state (columns, sort, query). Pass both or neither. */
  tableState?: TableState;
  onTableStateChange?: (next: TableState) => void;
  /** Hide the Columns button (a page that has no column controls). */
  hideColumnsMenu?: boolean;
  /** Controls drawn right after the search input (the list's filters). */
  toolbar?: ReactNode;
  /**
   * Row selection (RELAY-947 bulk actions): pass both to get a checkbox column, pinned first.
   * The header box selects or clears the rows loaded so far.
   */
  selection?: ReadonlySet<string> | undefined;
  onSelectionChange?: ((next: Set<string>) => void) | undefined;
  /** Text width in px for the auto column widths; defaults to a canvas, or an estimate without one (jsdom). */
  measureText?: (text: string, kind: 'cell' | 'header') => number;
}

const SELECT_COLUMN = '__select';

const DEBOUNCE_MS = 250;
const AUTO_MIN = 72;
const AUTO_MAX = 320;
const CELL_PAD = 32; // px-4 on both sides
const HEADER_PAD = 48; // px-4 on both sides + the sort arrow and its gap
const MEASURE_CHARS = 200; // far past any max width; spares measuring a long note in full
const ROW_CLICK_DELAY_MS = 220;

/** Header and body rows are flex rows with fixed-width cells so they stay aligned; the last column absorbs any slack. */
/** The row-selection checkbox: brand-coloured, a fixed size so it never inherits a squeeze. */
const CHECKBOX = 'accent-brand size-4 shrink-0 cursor-pointer';

function colStyle<T>(column: Column<T, unknown>, last: boolean): React.CSSProperties {
  const size = column.getSize();
  const pinned = column.getIsPinned() === 'left';
  return {
    flex: `${last ? 1 : 0} 0 ${size}px`,
    width: size,
    minWidth: size,
    ...(pinned ? { position: 'sticky', left: column.getStart('left'), zIndex: 1 } : {}),
  };
}

let canvas: OffscreenCanvasRenderingContext2D | null | undefined;
let fontFamily = '';
/** Body text is text-sm, headers text-xs semibold (the classes below). */
function defaultMeasureText(text: string, kind: 'cell' | 'header'): number {
  const px = kind === 'header' ? 12 : 14;
  if (canvas === undefined) {
    canvas =
      typeof OffscreenCanvas === 'function' ? new OffscreenCanvas(1, 1).getContext('2d') : null;
    fontFamily = getComputedStyle(document.body).fontFamily || 'sans-serif';
  }
  if (!canvas) return text.length * px * 0.55;
  canvas.font = `${kind === 'header' ? 600 : 400} ${px}px ${fontFamily}`;
  return canvas.measureText(text).width;
}

/** The text a cell shows: its `cell` function's string, else its value; '' when neither is text. */
function cellText<T>(cell: Cell<T, unknown>): string {
  const render = cell.column.columnDef.cell;
  const shown: unknown = typeof render === 'function' ? render(cell.getContext()) : render;
  const text = typeof shown === 'string' || typeof shown === 'number' ? shown : cell.getValue();
  return typeof text === 'string' || typeof text === 'number' ? String(text) : '';
}

function toSort(sorting: SortingState): SortState | null {
  const s = sorting[0];
  return s ? { id: s.id, desc: s.desc } : null;
}
function fromSort(sort: TableState['sort']): SortingState {
  return sort ? [{ id: sort.field, desc: sort.dir === 'desc' }] : [];
}

export function RecordTable<T>({
  columns,
  source,
  getRowId,
  mode = 'infinite',
  pageSize = 100,
  rowHeight = 48,
  height = 'calc(100vh - 220px)',
  filterPlaceholder = 'Filter…',
  emptyMessage = 'No records.',
  initialRect,
  prefetchThreshold = 20,
  onRowClick,
  tableState,
  onTableStateChange,
  hideColumnsMenu = false,
  toolbar,
  selection,
  onSelectionChange,
  measureText = defaultMeasureText,
}: RecordTableProps<T>) {
  // ── State: controlled by the page, or internal ──────────────────────────────────────────
  const controlled = tableState !== undefined && onTableStateChange !== undefined;
  const [internal, setInternal] = useState<TableState>(EMPTY_TABLE_STATE);
  const state = controlled ? tableState : internal;
  const update = useCallback(
    (patch: Partial<TableState>) => {
      const next = { ...state, ...patch };
      if (controlled) onTableStateChange(next);
      else setInternal(next);
    },
    [controlled, onTableStateChange, state],
  );
  const setColumns = useCallback(
    (columnsState: ColumnState) => update({ columns: columnsState }),
    [update],
  );

  const [filterText, setFilterText] = useState(state.q ?? '');
  const filter = useDebounced(filterText, DEBOUNCE_MS);
  // The debounced text is the query; mirror it into the (controlled) state once it settles.
  useEffect(() => {
    if ((state.q ?? '') !== filter) update(filter ? { q: filter } : { q: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the debounced text changes
  }, [filter]);
  // A state change from outside (a saved view loaded) refreshes the input.
  useEffect(() => {
    if ((state.q ?? '') !== filterText && (state.q ?? '') !== filter) setFilterText(state.q ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react to external q only
  }, [state.q]);

  const sorting = useMemo(() => fromSort(state.sort), [state.sort]);
  const sort = useMemo(() => toSort(sorting), [sorting]);
  const setSorting = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const s = next[0];
      update({ sort: s ? { field: s.id, dir: s.desc ? 'desc' : 'asc' } : undefined });
    },
    [sorting, update],
  );

  // Paged mode: the cursor that produced each page we have been to; the last is the current page.
  // Kept with the data set it belongs to and reset during render, not in an effect: an effect
  // runs after the first request for a new filter, which would go out with the old cursor.
  const dataSet = JSON.stringify([sort, filter, pageSize, source.key, mode]);
  const [paging, setPaging] = useState({ dataSet, stack: [null] as (string | null)[] });
  const cursorStack = paging.dataSet === dataSet ? paging.stack : [null];
  const setCursorStack = (next: (stack: (string | null)[]) => (string | null)[]) =>
    setPaging({ dataSet, stack: next(cursorStack) });

  const baseKey = ['records', source.key, sort, filter, pageSize] as const;
  const fetchPage = useCallback(
    (cursor: string | null) => source.fetchPage({ cursor, limit: pageSize, sort, filter }),
    [source, pageSize, sort, filter],
  );

  const infinite = useInfiniteQuery({
    queryKey: [...baseKey, 'infinite'],
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last: PageResult<T>) => last.nextCursor,
    enabled: mode === 'infinite',
  });
  const currentCursor = cursorStack[cursorStack.length - 1] ?? null;
  const paged = useQuery({
    queryKey: [...baseKey, 'page', currentCursor],
    queryFn: () => fetchPage(currentCursor),
    enabled: mode === 'paged',
    // Keep the page on screen while the next one of the SAME data set loads, never across one.
    placeholderData: (prev, prevQuery) =>
      JSON.stringify(prevQuery?.queryKey.slice(0, -1)) === JSON.stringify([...baseKey, 'page'])
        ? prev
        : undefined,
  });

  const rows: T[] = useMemo(() => {
    if (mode === 'infinite') return infinite.data?.pages.flatMap((p) => p.items) ?? [];
    return paged.data?.items ?? [];
  }, [mode, infinite.data, paged.data]);
  const total = mode === 'infinite' ? infinite.data?.pages[0]?.total : paged.data?.total;
  const isPending = mode === 'infinite' ? infinite.isPending : paged.isPending;
  const error = mode === 'infinite' ? infinite.error : paged.error;
  const hasNext = mode === 'infinite' ? infinite.hasNextPage : Boolean(paged.data?.nextCursor);

  // ── Column state → TanStack ─────────────────────────────────────────────────────────────
  const columnIds = useMemo(
    () => columns.map((c) => c.id ?? (c as { accessorKey?: string }).accessorKey ?? ''),
    [columns],
  );
  const selectable = selection !== undefined && onSelectionChange !== undefined;
  const columnOrder = useMemo(
    () => [
      ...(selectable ? [SELECT_COLUMN] : []),
      ...state.columns.order.filter((id) => columnIds.includes(id)),
      ...columnIds.filter((id) => !state.columns.order.includes(id)),
    ],
    [state.columns.order, columnIds, selectable],
  );
  const columnVisibility = useMemo(
    () => Object.fromEntries(state.columns.hidden.map((id) => [id, false])),
    [state.columns.hidden],
  );
  const columnPinning = useMemo(
    () => ({
      left: [
        ...(selectable ? [SELECT_COLUMN] : []),
        ...state.columns.pinned.filter((id) => columnIds.includes(id)),
      ],
    }),
    [state.columns.pinned, columnIds, selectable],
  );
  const allColumns = useMemo<ColumnDef<T, unknown>[]>(() => {
    if (!selectable) return columns;
    const loadedIds = rows.map(getRowId);
    const allLoaded = loadedIds.length > 0 && loadedIds.every((id) => selection.has(id));
    const select: ColumnDef<T, unknown> = {
      id: SELECT_COLUMN,
      // Wide enough for the box itself: the column has no padding (below), and 40px with the
      // cells' 16px side padding left 8px, so the checkbox was cut in half (TS-1591).
      size: 48,
      enableResizing: false,
      header: () => (
        <input
          type="checkbox"
          className={CHECKBOX}
          aria-label="Select all loaded rows"
          checked={allLoaded}
          onChange={() => {
            const next = new Set(selection);
            for (const id of loadedIds) {
              if (allLoaded) next.delete(id);
              else next.add(id);
            }
            onSelectionChange(next);
          }}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          className={CHECKBOX}
          aria-label="Select row"
          checked={selection.has(row.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => {
            const next = new Set(selection);
            if (next.has(row.id)) next.delete(row.id);
            else next.add(row.id);
            onSelectionChange(next);
          }}
        />
      ),
    };
    return [select, ...columns];
  }, [selectable, columns, rows, getRowId, selection, onSelectionChange]);

  const table = useReactTable({
    data: rows,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableSortingRemoval: true,
    sortDescFirst: false, // every column cycles asc → desc → none (TanStack's default flips numbers)
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      columnSizing: state.columns.sizes,
      columnPinning,
    },
    onSortingChange: setSorting,
    onColumnSizingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(state.columns.sizes) : updater;
      setColumns({ ...state.columns, sizes: next });
    },
    getRowId,
  });

  // ── Column widths from content ──────────────────────────────────────────────────────────
  // Per row and column, the measured text width; rows already measured cost a lookup when a
  // page is appended. A new column set or measurer starts over.
  const widthCache = useMemo(
    () => new WeakMap<object, Record<string, number>>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are what invalidates the cache, not what builds it
    [columns, measureText],
  );
  const tableRows = table.getRowModel().rows;
  // While a new data set loads there are no rows: keep the last widths rather than jump.
  const lastAutoSizes = useRef<Record<string, number>>({});
  const autoSizes = useMemo(() => {
    if (tableRows.length === 0) return lastAutoSizes.current;
    const widest: Record<string, number> = {};
    for (const row of tableRows) {
      let widths = widthCache.get(row.original as object);
      if (!widths) {
        widths = {};
        for (const cell of row.getAllCells()) {
          if (cell.column.id === SELECT_COLUMN) continue;
          const text = cellText(cell);
          if (text) widths[cell.column.id] = measureText(text.slice(0, MEASURE_CHARS), 'cell');
        }
        widthCache.set(row.original as object, widths);
      }
      for (const [id, w] of Object.entries(widths)) widest[id] = Math.max(widest[id] ?? 0, w);
    }
    const sizes: Record<string, number> = {};
    columns.forEach((col, i) => {
      const id = columnIds[i] ?? '';
      const extra = col.meta?.autoSize ?? 0;
      const content = widest[id];
      if (extra === false || content === undefined) return;
      const label = typeof col.header === 'string' ? col.header : id;
      const width = Math.max(measureText(label, 'header') + HEADER_PAD, content + CELL_PAD + extra);
      const clamped = Math.min(Math.max(width, col.minSize ?? AUTO_MIN), col.maxSize ?? AUTO_MAX);
      sizes[id] = Math.ceil(clamped);
    });
    lastAutoSizes.current = sizes;
    return sizes;
  }, [tableRows, widthCache, columns, columnIds, measureText]);
  const columnSizing = useMemo(
    () => ({ ...autoSizes, ...state.columns.sizes }),
    [autoSizes, state.columns.sizes],
  );
  // The widths need the table's rows, so they reach its state after it is built; this is the
  // same call useReactTable makes each render. A drag still persists only `columns.sizes`
  // (onColumnSizingChange above applies the updater to those alone).
  table.setOptions((prev) => ({ ...prev, state: { ...prev.state, columnSizing } }));

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
    ...(initialRect ? { initialRect } : {}),
  });
  const virtualRows = virtualizer.getVirtualItems();

  // Infinite mode: ask for the next page when the last rendered row is near the end.
  const lastIndex = virtualRows[virtualRows.length - 1]?.index ?? -1;
  useEffect(() => {
    if (mode !== 'infinite' || !infinite.hasNextPage || infinite.isFetchingNextPage) return;
    if (rows.length > 0 && lastIndex >= rows.length - 1 - prefetchThreshold)
      void infinite.fetchNextPage();
  }, [mode, lastIndex, rows.length, prefetchThreshold, infinite]);

  // Changing the ordering, filter or data set restarts from the top.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [sort, filter, currentCursor, source.key]);

  // A row click fires after the double-click window so an inline cell edit (double-click,
  // RELAY-942) does not also open the row; the second click cancels the pending open.
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelRowClick = useCallback(() => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = null;
  }, []);
  const deferredRowClick = useCallback(
    (row: T) => {
      cancelRowClick();
      clickTimer.current = setTimeout(() => {
        clickTimer.current = null;
        onRowClick?.(row);
      }, ROW_CLICK_DELAY_MS);
    },
    [cancelRowClick, onRowClick],
  );
  useEffect(() => cancelRowClick, [cancelRowClick]);

  const sortable = (id: string) => source.sortable === undefined || source.sortable.includes(id);
  const headerLabel = (id: string) => {
    const col = columns.find((c) => (c.id ?? (c as { accessorKey?: string }).accessorKey) === id);
    return typeof col?.header === 'string' ? col.header : id;
  };

  return (
    <div className="flex flex-col gap-4" data-testid="record-table">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm min-w-48 flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2" />
          <Input
            aria-label="Filter records"
            placeholder={filterPlaceholder}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="pl-10"
          />
        </div>
        {toolbar}
        <span className="text-muted-foreground text-sm" data-testid="record-count">
          {total !== undefined
            ? `${total.toLocaleString()} record${total === 1 ? '' : 's'}`
            : `${rows.length.toLocaleString()} loaded`}
        </span>
        {(infinite.isFetchingNextPage || (paged.isFetching && !paged.isPending)) && (
          <Loader2
            className="text-muted-foreground size-4 animate-spin"
            aria-label="Loading more"
          />
        )}
        {!hideColumnsMenu && (
          <div className="ml-auto">
            <ColumnsMenu
              columnIds={columnIds}
              label={headerLabel}
              state={state.columns}
              onChange={setColumns}
            />
          </div>
        )}
      </div>

      <div className="bg-card border-border rounded-card shadow-card overflow-hidden border">
        <div
          ref={scrollRef}
          className="overflow-auto"
          style={{ height }}
          data-testid="record-scroll"
        >
          <table
            className="min-w-full border-collapse text-sm"
            style={{ width: table.getTotalSize() }}
          >
            <thead className="bg-muted sticky top-0 z-10 shadow-[inset_0_-1px_0_var(--color-border)]">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="flex w-full">
                  {hg.headers.map((header) => {
                    const canSort = sortable(header.column.id);
                    const dir = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        style={colStyle(header.column, header.index === hg.headers.length - 1)}
                        className={cn(
                          // A label wraps anywhere and the cell clips: one long word
                          // ("candidate_has_video_preview") spilled out of its column and made
                          // the scroll area wider than the header and rows, leaving a blank strip.
                          'text-muted-foreground relative min-w-0 overflow-hidden py-3 text-left text-xs font-semibold [overflow-wrap:anywhere] select-none',
                          header.column.id === SELECT_COLUMN ? 'flex justify-center px-0' : 'px-4',
                          // Opaque over what scrolls under it, so it draws the header's bottom
                          // line itself, and a divider where the scrolling columns go under.
                          header.column.getIsPinned() &&
                            'bg-muted shadow-[inset_0_-1px_0_var(--color-border),inset_-1px_0_0_var(--color-border)]',
                        )}
                        data-pinned={header.column.getIsPinned() || undefined}
                      >
                        {canSort ? (
                          <button
                            type="button"
                            className="hover:text-foreground inline-flex max-w-full items-center gap-1 text-left"
                            onClick={header.column.getToggleSortingHandler()}
                            aria-sort={
                              dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'
                            }
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {dir === 'asc' ? (
                              <ArrowUp className="size-3" />
                            ) : dir === 'desc' ? (
                              <ArrowDown className="size-3" />
                            ) : (
                              <ArrowUpDown className="size-3 opacity-40" />
                            )}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                        <div
                          role="separator"
                          aria-label={`Resize ${headerLabel(header.column.id)}`}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={cn(
                            'hover:bg-brand absolute top-0 right-0 h-full w-1 cursor-col-resize select-none',
                            header.column.getIsResizing() && 'bg-brand',
                          )}
                        />
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody
              style={{ height: virtualizer.getTotalSize() }}
              className="relative block"
              data-rows={tableRows.length}
              data-virtual={virtualRows.length}
            >
              {virtualRows.map((v) => {
                const row = tableRows[v.index];
                if (!row) return null;
                const cells = row.getVisibleCells();
                return (
                  <tr
                    key={row.id}
                    data-index={v.index}
                    ref={virtualizer.measureElement}
                    onClick={onRowClick ? () => deferredRowClick(row.original) : undefined}
                    // Capture phase: a cell's own double-click handler (inline edit) stops the
                    // event, and the pending open must still be cancelled before that happens.
                    onDoubleClickCapture={onRowClick ? cancelRowClick : undefined}
                    className={cn(
                      'border-border/70 hover:bg-surface-hover bg-card absolute left-0 flex w-full border-b transition-colors',
                      onRowClick && 'cursor-pointer',
                    )}
                    style={{ transform: `translateY(${v.start}px)`, height: rowHeight }}
                  >
                    {cells.map((cell, index) => (
                      <td
                        key={cell.id}
                        style={colStyle(cell.column, index === cells.length - 1)}
                        className={cn(
                          'flex items-center overflow-hidden',
                          cell.column.id === SELECT_COLUMN ? 'justify-center px-0' : 'px-4',
                          // Divider where the scrolling columns slide under the pinned ones.
                          cell.column.getIsPinned() &&
                            'bg-inherit shadow-[inset_-1px_0_0_var(--color-border)]',
                        )}
                        data-pinned={cell.column.getIsPinned() || undefined}
                      >
                        {/* A block child is what truncates: text-overflow does nothing on a flex
                            container's own children, so long text was clipped with no ellipsis. */}
                        {cell.column.id === SELECT_COLUMN ? (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        ) : (
                          <div className="min-w-0 flex-1 truncate">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isPending && (
            <div className="text-muted-foreground flex items-center justify-center gap-2 py-10 text-sm">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </div>
          )}
          {error != null && (
            <div className="text-danger px-3 py-6 text-sm" role="alert">
              {describeError(error)}
            </div>
          )}
          {!isPending && error == null && rows.length === 0 && (
            <div className="text-muted-foreground px-3 py-10 text-center text-sm">
              {emptyMessage}
            </div>
          )}
        </div>
      </div>

      {mode === 'paged' && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={cursorStack.length <= 1}
            onClick={() => setCursorStack((s) => s.slice(0, -1))}
          >
            <ChevronLeft /> Previous
          </Button>
          <span className="text-muted-foreground text-sm">Page {cursorStack.length}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => {
              const next = paged.data?.nextCursor;
              if (next) setCursorStack((s) => [...s, next]);
            }}
          >
            Next <ChevronRight />
          </Button>
        </div>
      )}
    </div>
  );
}

export type { ColumnDef, PageRequest, PageResult, RecordSource, SortState };
